import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { type Chart as ChartJs } from "chart.js";
import { Line } from "react-chartjs-2";

import { useUser } from "@components/user-context";
import { useMetricsExplorerContext } from "@features/metrics-explorer/hooks";

import { BackgroundPlugin, TitlesPlugin } from "./chart-plugins";
import {
  generateChartData,
  generateChartDataset,
  getChartOptions,
  getThemeColors,
} from "./chart-utils";

export const Chart = () => {
  const { user } = useUser();

  const chartRef = useRef<ChartJs<"line", any> | null>(null);
  const [key, setKey] = useState(0);
  const [isDarkMode, setIsDarkMode] = useState(
    document.body.classList.contains("dark-mode"),
  );

  // Initial render
  useEffect(() => {
    // Set initial dark mode
    setIsDarkMode(document.body.classList.contains("dark-mode"));
    // Force initial render after a short delay
    const timer = setTimeout(() => {
      setKey((prev) => prev + 1);
    }, 10);
    return () => clearTimeout(timer);
  }, []);

  // Update chart when theme changes
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.body.classList.contains("dark-mode"));
      if (chartRef.current && chartRef.current.scales) {
        chartRef.current.update();
      } else {
        // Force re-render if chart isn't ready
        setKey((k) => k + 1);
      }
    });
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  const setChartInstance = useCallback(
    (instance: ChartJs<"line", any> | null | undefined) => {
      if (instance) {
        chartRef.current = instance;
      }
    },
    [],
  );

  const {
    metricsData,
    benchmarksData,
    indexData,
    tiles,
    chartState: { alignAxesScale },
    filters: {
      attributionModel,
      attributionWindow,
      accountingMode,
      filterQuery,
      timeGranularity,
      dateRangeInterval,
    },
    buildMetrics,
  } = useMetricsExplorerContext();

  // Get current theme colors
  const themeColors = useMemo(() => getThemeColors(), [isDarkMode]);

  const { options, chartData } = useMemo(() => {
    const chartData = generateChartData({
      metricsData,
      benchmarksData,
      indexData,
      tiles,
      attributionModel,
      attributionWindow,
      accountingMode,
      timeGranularity,
      dateRange: dateRangeInterval,
      timezone: user.timezone,
      buildMetrics,
    });

    const options = getChartOptions({
      attributionModel,
      attributionWindow,
      accountingMode,
      filterQuery,
      timeGranularity,
      alignAxes: alignAxesScale,
      data: chartData,
      code: user.currencies.currentCurrency,
      user,
      themeColors,
    });

    return { options, chartData };
  }, [
    metricsData,
    benchmarksData,
    indexData,
    tiles,
    attributionModel,
    attributionWindow,
    accountingMode,
    filterQuery,
    timeGranularity,
    dateRangeInterval,
    alignAxesScale,
    user,
    buildMetrics,
    isDarkMode,
  ]);

  const chartDataset = useMemo(() => {
    if (!chartRef.current) {
      return { labels: [], datasets: [] };
    }

    const chartDataset = generateChartDataset(
      chartRef.current,
      chartData,
      dateRangeInterval,
      timeGranularity,
      user.timezone,
    );

    return chartDataset;
  }, [chartRef, chartData, timeGranularity, dateRangeInterval, user.timezone]);

  return (
    <Line
      key={key}
      ref={setChartInstance}
      datasetIdKey="id"
      plugins={[BackgroundPlugin, TitlesPlugin]}
      options={options}
      data={chartDataset}
    />
  );
};
